/**
 * The wizard's phone chrome. Three adaptations carry the whole flow, per the
 * mobile artboards: the five-step stepper collapses to "Step n of 5" with a
 * progress bar, every side-by-side row stacks, and the primary action moves to
 * a persistent footer where a thumb can reach it.
 *
 * Logic is unchanged from desktop — this is the same wizard, laid out for
 * 390px.
 */

import React from "react";
import { Box, Typography } from "@mui/material";
import { bulkMobileStyles } from "../BulkSend.styles";
import { BULK_STEPS } from "../BulkSendTypesAndValidation";
import { ChevronRightIcon } from "./BulkSendIcons";

const s = bulkMobileStyles;

interface HeaderProps {
  /** Which step the user is on, 0-based. */
  currentStep: number;
  onBack?: () => void;
  onClose: () => void;
}

export const MobileWizardHeader: React.FC<HeaderProps> = ({
  currentStep,
  onBack,
  onClose,
}) => {
  /* Every door walks all five steps now, so the count is the step itself. */
  const steps = BULK_STEPS;
  const stepName = steps[currentStep];
  const position = currentStep + 1;
  const nextName = steps[currentStep + 1];

  return (
    <Box component="header" sx={s.header}>
      <Box sx={s.headerRow}>
        <Box
          component="button"
          type="button"
          aria-label={onBack ? "Back a step" : "Close bulk send"}
          onClick={onBack ?? onClose}
          sx={s.backButton}
        >
          <Box sx={{ transform: "rotate(180deg)", display: "flex" }}>
            <ChevronRightIcon size={17} strokeWidth={2.2} />
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="span" sx={s.headerTitle}>
            New bulk send
          </Typography>
        </Box>
      </Box>

      <Box sx={s.stepperBlock}>
        <Box sx={s.stepperRow}>
          <Box component="span" sx={s.stepCount}>
            Step {position} of {steps.length}
          </Box>
          <Box component="span" sx={s.stepName}>
            {stepName}
          </Box>
          {nextName && (
            <Box component="span" sx={s.stepNext}>
              Next: {nextName}
            </Box>
          )}
        </Box>
        <Box
          sx={s.bars}
          role="img"
          aria-label={`Step ${position} of ${steps.length}`}
        >
          {steps.map((label, index) => (
            <Box key={label} sx={s.bar(index < position)} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

interface FooterProps {
  primaryLabel: string;
  onPrimary: () => void;
  primaryDisabled?: boolean;
  secondaryLabel?: string;
  onSecondary?: () => void;
}

export const MobileWizardFooter: React.FC<FooterProps> = ({
  primaryLabel,
  onPrimary,
  primaryDisabled,
  secondaryLabel,
  onSecondary,
}) => (
  <Box sx={s.footer}>
    {secondaryLabel && onSecondary && (
      <Box
        component="button"
        type="button"
        onClick={onSecondary}
        sx={s.footerSecondary}
      >
        {secondaryLabel}
      </Box>
    )}
    <Box
      component="button"
      type="button"
      onClick={onPrimary}
      disabled={primaryDisabled}
      sx={s.footerPrimary(Boolean(primaryDisabled))}
    >
      {primaryLabel}
    </Box>
  </Box>
);

export default MobileWizardHeader;
